import React, { Suspense, lazy } from "react";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { DataProvider } from "./context/DataContext";
import { UnreadProvider } from "./context/UnreadContext";
import { UnreadChatsProvider } from "./context/UnreadChatsContext";
import { ToastProvider } from "./components/Toast";
import { PageSpinner } from "./components/primitives";
import Shell from "./components/Shell";
import TestBuildBadge from "./components/TestBuildBadge";
import AuthRoutes from "./pages/auth/AuthRoutes";
import { returnPath } from "./pages/auth/authUtils";
import ClubsPage from "./pages/ClubsPage";
import ClubPage from "./pages/ClubPage";
import CreateClubPage from "./pages/CreateClubPage";
import ManagePage from "./pages/ManagePage";
import SuperAdminPage from "./pages/SuperAdminPage";

// W2/W3 sections, lazy so each lands in its own chunk. Every path below is the section's
// final page file; sections never edit this file.
const EventsPage = lazy(() => import("./pages/EventsPage"));
const EventPage = lazy(() => import("./pages/EventPage"));
const ProfilePage = lazy(() => import("./pages/ProfilePage"));
const MyClubsPage = lazy(() => import("./pages/MyClubsPage"));
const UserProfilePage = lazy(() => import("./pages/UserProfilePage"));
const FriendsPage = lazy(() => import("./pages/FriendsPage"));
const FindStudentsPage = lazy(() => import("./pages/FindStudentsPage"));
const PartnersPage = lazy(() => import("./pages/partners/PartnersPage"));
const PartnersAllPage = lazy(() => import("./pages/partners/PartnersAllPage"));
const SponsorPage = lazy(() => import("./pages/partners/SponsorPage"));
const NotificationsPage = lazy(() => import("./pages/NotificationsPage"));
const SettingsPage = lazy(() => import("./pages/SettingsPage"));
const NotificationSettingsPage = lazy(() => import("./pages/NotificationSettingsPage"));
const ChatsPage = lazy(() => import("./pages/chats/ChatsPage"));

function AppRoutes() {
  const { user, loading, emailVerified } = useAuth();
  const location = useLocation();

  if (loading) return <PageSpinner />;

  // Signed out -> auth pages (remember where they were headed).
  if (!user) {
    return (
      <Routes>
        <Route path="/auth/*" element={<AuthRoutes />} />
        <Route path="*" element={<Navigate to="/auth/login" replace state={{ from: location }} />} />
      </Routes>
    );
  }

  // Signed in but email not verified yet.
  if (!emailVerified) {
    return (
      <Routes>
        <Route path="/auth/*" element={<AuthRoutes />} />
        <Route path="*" element={<Navigate to="/auth/verify" replace state={{ from: location }} />} />
      </Routes>
    );
  }

  return (
    <Suspense fallback={<PageSpinner />}>
      <Routes>
        <Route path="/auth/*" element={<Navigate to={returnPath(location.state) || "/clubs"} replace />} />
        <Route element={<Shell />}>
          <Route index element={<Navigate to="/clubs" replace />} />
          <Route path="clubs" element={<ClubsPage />} />
          <Route path="clubs/new" element={<CreateClubPage />} />
          <Route path="clubs/:clubId" element={<ClubPage />} />
          <Route path="manage" element={<ManagePage />} />
          <Route path="manage/:clubId/*" element={<ManagePage />} />
          <Route path="super" element={<SuperAdminPage />} />
          <Route path="events" element={<EventsPage />} />
          <Route path="events/:eventId" element={<EventPage />} />
          <Route path="profile" element={<ProfilePage />} />
          <Route path="profile/clubs" element={<MyClubsPage />} />
          <Route path="users/:userId" element={<UserProfilePage />} />
          <Route path="friends" element={<FriendsPage />} />
          <Route path="friends/find" element={<FindStudentsPage />} />
          <Route path="partners" element={<PartnersPage />} />
          <Route path="partners/all" element={<PartnersAllPage />} />
          <Route path="partners/:businessId" element={<SponsorPage />} />
          <Route path="notifications" element={<NotificationsPage />} />
          <Route path="settings" element={<SettingsPage />} />
          <Route path="settings/notifications" element={<NotificationSettingsPage />} />
          <Route path="chats" element={<ChatsPage />} />
          <Route path="chats/:kind/:chatId" element={<ChatsPage />} />
          <Route path="*" element={<Navigate to="/clubs" replace />} />
        </Route>
      </Routes>
    </Suspense>
  );
}

// Everything that holds per-account state (DataContext, the unread providers, every page and
// hook) is keyed by the verified uid: an account switch (e.g. from another tab) remounts it all,
// so no list, cache, listener or role from the previous account can survive into the new one.
// Signed-out and unverified sessions share one key so the sign-up / verify flow is NOT remounted
// when createUser signs in mid-submit.
function AccountScope({ children }) {
  const { user, emailVerified } = useAuth();
  return <DataProvider key={user && emailVerified ? user.uid : "auth"}>{children}</DataProvider>;
}

// Provider order (cross-check.md F1): AccountScope > UnreadProvider (the one unread-notifications
// listener) > UnreadChatsProvider (consumes that listener's feed) > ToastProvider.
export default function App() {
  return (
    <BrowserRouter>
      <TestBuildBadge />
      <AuthProvider>
        <AccountScope>
          <UnreadProvider>
            <UnreadChatsProvider>
              <ToastProvider>
                <AppRoutes />
              </ToastProvider>
            </UnreadChatsProvider>
          </UnreadProvider>
        </AccountScope>
      </AuthProvider>
    </BrowserRouter>
  );
}
